<template>
    <div class="container-onboard">

        <div class="flex-item">
            <div v-if="toast" class="onboarding-toast">
                <div :class="{'success': !onboardingError, 'error':  onboardingError}">{{ toastValue }} </div>
            </div>
            <div class="onboard-header-section mt-3">
                <payout-requirements-reminder
                  v-if="
                    $store.getters['onboarding/step1AndStep2Completed'] &&
                    !$store.getters['onboarding/step3Completed']
                  "
                />
                <p class="title-onboard mt-3">Get Started!</p>
                <p class="subtitle-onboard">Accomplish these steps to finish your eStore website's basic setup and unlock
                    more settings for your eStore.</p>
            </div>
            <!-- step 1  -->
            <div class="step-wrapper">

                <div class="step">
                    <div class="inner-step">
                        <div class="shop-icon">
                            <img src="@/assets/images/onboarding/shop.svg" alt="shop">
                        </div>
                        <div class="shop-instruction">
                            <p class="shop-configure">Configure your shop</p>
                            <p class="shop-configure-sub">Build your online store</p>
                        </div>

                    </div>
                    <div>
                        <p class="start-indicator" :class="{ 'started': merchantStatus.progress[0].step_status === 'In Progress',
                            'completed': merchantStatus.progress[0].step_status === 'Completed' }">{{
                            merchantStatus.progress[0].step_status | removeYet }}
                        </p>
                    </div>
                </div>
                <div>
                    <OnboardingCollapseSteps :dropdown="false" class=" item" :collapseId="'collapse1'"
                        :isOpen="isCollapse1Open">
                        <template slot="step-number">
                            <div class="collapse-icon">
                                <img :src="getImageSrc(0,0,1)" alt="number-1">
                            </div>
                        </template>
                        <template slot="title">
                            <div class="title-container">
                                <span class="title">Set up your branch and location</span>
                                <span class="sub-title">Set up your store's location and contact information so your
                                    customers
                                    know where and how to reach you. This step is required before you can
                                    add your products and start selling.</span>
                            </div>
                        </template>
                        <template slot="collapse-content">
                            <div class="collapse-content">
                                <OnboardingFrom class="location-form" />
                                <div class="collapse-footer">
                                    <p>You can add more information about your store, including your operating
                                        hours and employees, from the Stores module. <router-link to="/store/organization"
                                            @click.native="startLoading"><span v-if="linkLoading" class="link-loading">
                                                ...
                                            </span><span v-if="!linkLoading" class="store-link">Go to
                                                Stores ></span></router-link>
                                    </p>
                                </div>
                            </div>
                        </template>
                    </OnboardingCollapseSteps>
                </div>
                <div>
                    <OnboardingCollapseSteps class="item" :dropdown="!$store.getters['onboarding/getStep1SubStep1']"
                        :collapseId="'collapse2'" :isOpen="isCollapse2Open">
                        <template slot="step-number">
                            <div class="collapse-icon">
                                <img :src="getImageSrc(0,1,2)" alt="number-2">
                            </div>
                        </template>
                        <template slot="title">
                            <div class="title-container">
                                <span class="title">Add your first product</span>
                                <span class="sub-title">Upload your first product to start building your online
                                    catalog</span>
                            </div>
                        </template>
                        <template slot="collapse-content">
                            <div class="collapse-content">
                                <p class="collapse-footer">Before uploading your first product, make sure that you have
                                    completely
                                    set up your branch. If you have not completed your branch setup, go back
                                    to the previous step.</p>
                                <b-button @click="createProduct" class="substeps-btn docu"> <span class="plus-icon"><img
                                            src="@/assets/images/plus-icon.svg" alt="plus-icon"></span>Create a
                                    product</b-button>
                            </div>
                        </template>
                    </OnboardingCollapseSteps>
                </div>
                <div>
                    <OnboardingCollapseSteps class="item" :dropdown="false" :collapseId="'collapse3'"
                        :isOpen="isCollapse3Open">
                        <template slot="step-number">
                            <div class="collapse-icon">
                                <img :src="getImageSrc(0,2,3)" alt="number-3">
                            </div>
                        </template>
                        <template slot="title">
                            <div class="title-container">
                                <span class="title">Design your eStore website</span>
                                <span class="sub-title">Customize the design of your eStore website</span>
                            </div>
                        </template>
                        <template slot="collapse-content">
                            <div class="collapse-content">
                                <p class="collapse-footer">To start designing your e-store, simply navigate to the
                                    <strong>Design</strong>
                                    section
                                </p>
                                <b-button @click="createWebsite" class="substeps-btn docu">Start Designing <span
                                        class="arrow-icon"><img src="@/assets/images/upgrade-plan-icon.svg" alt="arrow">
                                    </span></b-button>
                            </div>
                        </template>
                    </OnboardingCollapseSteps>
                </div>

            </div>
            <!-- step 2 -->
            <div class="step-wrapper">

                <div class="step">
                    <div class="inner-step">
                        <div class="shop-icon">
                            <img src="@/assets/images/onboarding/fulfillment.svg" alt="shop">
                        </div>
                        <div class="shop-instruction">
                            <p class="shop-configure">Setup your fulfillment</p>
                            <p class="shop-configure-sub">Select how you want your customers to receive their orders. Choose
                                atleast one fulfillment method to proceed</p>
                        </div>

                    </div>
                    <div>
                        <p class="start-indicator" :class="{ 'started': merchantStatus.progress[1].step_status === 'In Progress',
                            'completed': merchantStatus.progress[1].step_status === 'Completed' }">{{
                            merchantStatus.progress[1].step_status | removeYet }}
                        </p>
                    </div>
                </div>
                <div>
                    <OnboardingCollapseSteps class="item" :dropdown="false"
                        :class="{'fulfillment-form-error': logisticError}" :collapseId="'collapse4'"
                        :isOpen="isCollapse4Open">

                        <template slot="step-number">
                            <div class="collapse-icon">
                                <img :src="getImageSrc(1,0,1)" alt="number-1">
                            </div>
                        </template>
                        <template slot="title">
                            <div class="title-container">
                                <span class="title">Choose your fulfillment methods</span>
                                <span class="sub-title">Setup your order fulfillment methods</span>
                            </div>
                        </template>
                        <template slot="collapse-content">

                            <b-form @submit.prevent="fulfillmentOnSubmit">
                                <div class="collapse-content">
                                    <div v-if="logisticError" class="error-message logistic-error">
                                        <div class="error-title">
                                            <div><img src="@/assets/images/onboarding/errorIcon.svg" alt="error"></div>
                                            <p>Select atleast one logistic type</p>
                                        </div>
                                        <div>
                                            <img src="@/assets/images/onboarding/closeIconSmall.svg" alt="close">
                                        </div>
                                    </div>
                                    <div class="fulfillment-item">
                                        <div>Deliver Now</div>
                                        <OnboardingSwitch v-model="toggleValue1" id="1" />
                                    </div>
                                    <div class="fulfillment-item">
                                        <div>Pickup Now</div>
                                        <OnboardingSwitch v-model="toggleValue2" id="2" />
                                    </div>
                                    <div class="fulfillment-item">
                                        <div>Scheduled Delivery</div>
                                        <OnboardingSwitch v-model="toggleValue3" id="3" />
                                    </div>
                                    <div class="fulfillment-item">
                                        <div> Scheduled Pickup</div>
                                        <OnboardingSwitch v-model="toggleValue4" id="4" />
                                    </div>

                                    <b-button :disabled="logisticBtnDisable" type="submit" class="substeps-btn"
                                        :class="{'disable-btn': logisticBtnDisable}"><span v-if="!loading2">Save</span>
                                        <span v-if="loading2"><img class=" spinner"
                                                src="@/assets/images/onboarding/loading.svg"
                                                alt="loading"></span></b-button>

                                </div>

                                <div class="footer-link">You can add more settings for order pick-up and delivery from the
                                    Order
                                    Fulfillment module. <router-link to="/fulfillment/delivery"
                                        @click.native="startLoading"><span v-if=" linkLoading" class="link-loading">
                                            ...
                                        </span><span v-if="!linkLoading" class="store-link">Go
                                            to Order Fulfillment
                                            ></span></router-link></div>
                            </b-form>


                        </template>
                    </OnboardingCollapseSteps>
                </div>

                <div>
                    <OnboardingCollapseSteps class="item" :dropdown="false" :collapseId="'collapse5'"
                        :isOpen="isCollapse5Open">

                        <template slot="step-number">
                            <div class="collapse-icon">
                                <img :src="getImageSrc(1,1,2)" alt="number-2">
                            </div>
                        </template>
                        <template slot="title">
                            <div class="title-container">
                                <span class="title">Set up your delivery fees</span>
                                <span class="sub-title">Set the applicable delivery fees for on-demand or scheduled
                                    delivery</span>
                            </div>
                        </template>
                        <template slot="collapse-content">
                            <div class="collapse-content">

                                <b-form @submit.prevent="feesOnSubmit">
                                    <div class="fees-content">

                                        <template v-slot:label>
                                            <p class="required-sign">Base Fee Value <span class="text-danger">*</span>
                                            </p>
                                        </template>
                                        <b-input-group class="custom-input-group"
                                            :class="{'fees-border-error':  fulfillmentFeesError || lessThan70 || baseValueNotEmpty || baseValueNotZero }">
                                            <template #prepend>
                                                <span class="custom-prepend">Php</span>
                                            </template>
                                            <b-form-input @keydown="handleKeydownWithDecimal" type="text"
                                                v-model="baseFeeValue" class="set-fees"></b-form-input>
                                        </b-input-group>
                                        <div v-if="fulfillmentFeesError || baseValueNotEmpty" class="error-message">Base
                                            Fee Value is required
                                        </div>
                                        <div v-if="baseValueNotZero" class="error-message">
                                            Base Fee Value should not negative number
                                        </div>


                                        <template v-slot:label>
                                            <p class="required-sign">Added Value Per Kilometer<span
                                                    class="text-danger">*</span>
                                            </p>
                                        </template>
                                        <b-input-group class="custom-input-group"
                                            :class="{'fees-border-error':  addedValueNotEmpty || addedValueNotZero }">
                                            <template #prepend>
                                                <span class=" custom-prepend">Php</span>
                                            </template>
                                            <b-form-input @keydown="handleKeydownWithDecimal" type="text"
                                                v-model="addedValueFee" class="set-fees"></b-form-input>
                                        </b-input-group>
                                        <div v-if="addedValueNotEmpty" class="error-message">Added Value Per Kilometer is
                                            required
                                        </div>
                                        <div v-if="addedValueNotZero" class="error-message">
                                            Added Value Per Kilometer should not negative number
                                        </div>
                                        <b-button type="submit" class="substeps-btn"><span v-if="!loading3">Save</span>
                                            <span v-if="loading3"><img class="spinner"
                                                    src="@/assets/images/onboarding/loading.svg"
                                                    alt="loading"></span></b-button>
                                    </div>

                                </b-form>



                                <div class="fees-details collapse-footer">You can set up dynamic fees and add more settings
                                    for order
                                    pick-up and
                                    delivery from the Order Fulfillment module. <router-link to="/fulfillment/fees"
                                        @click.native="startLoading">
                                        <span v-if="linkLoading" class="link-loading">...</span><span v-if="!linkLoading"
                                            class="store-link">Go
                                            to Order Fulfillment
                                            ></span></router-link> </div>
                            </div>
                        </template>
                    </OnboardingCollapseSteps>
                </div>

            </div>
            <!-- step 3 -->
            <div class="step-wrapper no-border-left">

                <div class="step">
                    <div class="inner-step">
                        <div class="shop-icon">
                            <img src="@/assets/images/onboarding/payout.svg" alt="shop">
                        </div>
                        <div class="shop-instruction">
                            <p class="shop-configure">Complete your payout information <span
                                    class="option">(Optional)</span></p>
                            <p class="shop-configure-sub">Upload your payout requirements</p>
                        </div>

                    </div>
                    <div>
                        <p class="start-indicator" :class="{ 'started': merchantStatus.progress[2].step_status === 'In Progress',
                            'completed': merchantStatus.progress[2].step_status === 'Completed' }">{{
                            merchantStatus.progress[2].step_status | removeYet }}
                        </p>
                    </div>
                </div>
                <div>
                    <OnboardingCollapseSteps class="item" :dropdown="false" :collapseId="'collapse6'"
                        :isOpen="isCollapse6Open">
                        <template slot="step-number">
                            <div class="collapse-icon">
                                <img :src="getImageSrc(2,0,1)" alt="number-1">
                            </div>
                        </template>
                        <template slot="title">
                            <div class="title-container">
                                <span class="title">Add your payout details</span>
                                <span class="sub-title">Input your bank information and upload the necessary documents to
                                    ensure that your eStore earnings are remitted to the correct account.</span>
                            </div>

                        </template>
                        <template slot="collapse-content">
                            <div class="collapse-content">
                                <div class="payout-details collapse-footer">You may choose to complete this step later.
                                    However, you will
                                    only start
                                    receiving your eStore earnings once you have completed this step.</div>
                                <router-link :to="'/onboarding/payout/requirements'"><b-button class="substeps-btn docu">Go
                                        to Documents <span class="arrow-icon"><img
                                                src="@/assets/images/upgrade-plan-icon.svg"
                                                alt="arrow"></span></b-button></router-link>

                            </div>
                        </template>
                    </OnboardingCollapseSteps>
                </div>
            </div>

        </div>
        <div class="flex-item">
            <div>
                <OnboardingOverallProgress />
            </div>


        </div>
        <div v-if="moduleAvailableModal && localModal">
            <SuccessModal />
        </div>

    </div>
</template>
<script>
import { mapState, mapActions } from "vuex";
import OnboardingOverallProgress from "@/components/onboarding/OnboardingOverallProgress";
import OnboardingCollapseSteps from "@/components/onboarding/OnboardingCollapseSteps";
import OnboardingFrom from "@/components/onboarding/OnboardingForm";
import OnboardingSwitch from "@/components/onboarding/OnboardingSwitch";
import SuccessModal from "@/components/onboarding/SuccessModal"
import LiveSellingModal from "@/components/onboarding/LiveSellingModal"
import PayoutRequirementsReminder from "@/components/onboarding/PayoutRequirementsReminder";



export default {

    name: "OnboardPage",
    components: {
        OnboardingOverallProgress,
        OnboardingCollapseSteps,
        OnboardingFrom,
        OnboardingSwitch,
        SuccessModal,
        LiveSellingModal,
        PayoutRequirementsReminder
    },

    data() {
        return {
            isCollapse1Open: true,
            isCollapse2Open: false,
            isCollapse3Open: false,
            isCollapse4Open: false,
            isCollapse5Open: false,
            isCollapse6Open: false,
            toggleValue1: false,
            toggleValue2: false,
            toggleValue3: false,
            toggleValue4: false,
            baseFeeValue: 70.00,
            addedValueFee: 7.00,
            collapseDropdown: false,
            addedValueNotEmpty: false,
            baseValueNotEmpty: false,
            baseValueNotZero: false,
            addedValueNotZero: false,
            lessThan70: false,
            localModal: false,
            linkLoading: false,

        };
    },
    computed: {
        ...mapState("onboarding", {
            merchantStatus: state => state.merchantStatus,
            onboardingError: state => state.onboardingError,
            toast: state => state.toast,
            toastValue: state => state.toastValue,
            loading2: state => state.loading2,
            loading3: state => state.loading3,
            fulfillmentStatus: state => state.fulfillmentStatus,
            fulfillmentSettings: state => state.fulfillmentSettings,
            moduleAvailableModal: state => state.moduleAvailableModal,
            loading: state => state.loading,
            logisticError: state => state.logisticError,
            fulfillmentFeesError: state => state.fulfillmentFeesError,
        }),
        logisticBtnDisable() {
            return !!(this.toggleValue1 === false && this.toggleValue2 === false && this.toggleValue3 === false && this.toggleValue4 === false);
        }
    },
    methods: {
        ...mapActions("onboarding", [
            "getMerchantSetup",
            "getProvinces",
            "getCities",
            "getBranchList",
            "getWebList",
            "getFulfillment",
            "getFulfillmentFees",
            "getPayoutDetails",
            "getFulfillmentSetupList",
            "updateFulfillment",
            "saveFulfillmentFeesSettings"
        ]),
        startFulfillmentWatchers() {
            this.$watch(
                () => this.fulfillmentStatus,
                (newValue) => {
                    if (newValue) {
                        this.loadfulFillmentSwitch();
                    }
                },
                { immediate: true }
            );

            this.$watch(
                () => this.fulfillmentSettings,
                (newValue) => {
                    if (newValue) {
                        this.loadFulfillmentFees();
                    }
                },
                { immediate: true }
            );
        },
        async fulfillmentOnSubmit() {

            const payload = {
                "delivery": {
                    "deliver_now": this.toggleValue1,
                    "scheduled": this.toggleValue3
                },
                "pickup": {
                    "pickup_now": this.toggleValue2,
                    "scheduled": this.toggleValue4
                },
            }

            await this.updateFulfillment({ payload: payload })
            await this.getMerchantSetup()


        },
        fulfillmentValidateForm() {
            let status = false
            if (this.baseFeeValue === '') {
                this.baseValueNotEmpty = true
                status = true
            }
            if (this.addedValueFee === '') {
                this.addedValueNotEmpty = true
                status = true
            }
            if (this.addedValueFee < 0) {
                this.addedValueNotZero = true
                status = true
            }
            if (this.baseFeeValue <= 0 && this.baseFeeValue !== '') {
                this.baseValueNotZero = true
                status = true
            }
            return status

        },
        async feesOnSubmit() {
            this.$store.commit('onboarding/setLogisticError', false)
            this.$store.commit('onboarding/setFulfillmentFeesError', false)
            this.baseValueNotEmpty = false
            this.addedValueNotEmpty = false
            this.baseValueNotZero = false
            this.addedValueNotZero = false
            this.lessThan70 = false
            const payload = {
                "level": "merchant",
                "deliver_now": {
                    "delivery_fee_type": "dynamic",
                    "delivery_base_value": this.baseFeeValue,
                    "delivery_base_km_range": this.addedValueFee,
                    "delivery_base_km": this.addedValueFee,
                    "minimum_spend_amount": 10,
                    "enable_minimum_spend": 1,
                    "minimum_spend_currency": "PHP",
                }
            }
            if (!this.fulfillmentValidateForm()) {
                await this.saveFulfillmentFeesSettings({ payload: payload })
                await this.getMerchantSetup()
            }





        },
        getImageSrc(progressIndex, subStepIndex, number) {
            let status = false
            if (this.merchantStatus.progress[progressIndex].substeps[subStepIndex].status === "Completed") {
                status = true
            }
            return status ? require("@/assets/images/onboarding/complete.svg") : require(`@/assets/images/onboarding/number${number}.svg`);
        },
        loadfulFillmentSwitch() {
            this.toggleValue1 = JSON.parse(this.fulfillmentStatus.delivery.deliver_now.toLowerCase());
            this.toggleValue2 = JSON.parse(this.fulfillmentStatus.pickup.pickup_now.toLowerCase());
            this.toggleValue3 = JSON.parse(this.fulfillmentStatus.delivery.scheduled.toLowerCase());
            this.toggleValue4 = JSON.parse(this.fulfillmentStatus.pickup.scheduled.toLowerCase());
        },

        loadFulfillmentFees() {
            if (!this.$store.getters['onboarding/getStep2Substep2']) {
                this.baseFeeValue = 70.00
                this.addedValueFee = 7.00
            }
            else {
                if (this.fulfillmentSettings.deliver_now.base_fee_value === '' || this.fulfillmentSettings.deliver_now.base_fee_value === null) {
                    this.baseFeeValue = 70.00
                } else {
                    this.baseFeeValue = this.fulfillmentSettings.deliver_now.base_fee_value
                }
                if (this.fulfillmentSettings.deliver_now.added_value_per_km === '' || this.fulfillmentSettings.deliver_now.added_value_per_km === null) {
                    this.addedValueFee = 7.00
                }
                else {
                    this.addedValueFee = this.fulfillmentSettings.deliver_now.added_value_per_km
                }
            }

        },

        createProduct() {
            this.$router.push("/products/create?mode=onboarding")
        },
        createWebsite() {
            this.$router.push("/design/web/create-web-design?mode=onboarding")
        },
        handleKeydownWithDecimal(event) {
            // Allow only numeric characters and certain key codes
            if (
                !/^[0-9.]*$/.test(event.key) && // Numeric characters
                event.keyCode !== 8 && // Backspace
                event.keyCode !== 46 && // Delete
                event.keyCode !== 37 && // Left arrow
                event.keyCode !== 39 // Right arrow
            ) {
                event.preventDefault();
            }

        },
        loadInitializeData() {
            const isInitialized = localStorage.getItem('isInitialized');

            if (!isInitialized) {
                // Perform your one-time initialization logic here
                this.initializeData();

                // Set a flag in localStorage to indicate that initialization has been done
                localStorage.setItem('isInitialized', 'true');

                window.location.reload()
            }
        },
        initializeData() {
            localStorage.setItem('successModal', 'true')
        },
        startLoading() {
            // Set loading to true when router-link is clicked
            this.linkLoading = true;

            // Simulate an async operation (e.g., API request)
            setTimeout(() => {
                // After a delay, set linkLoading back to false
                this.linkLoading = false;
            }, 2500); // Simulated delay of 2 seconds
        },

    },
    created() {
        this.startFulfillmentWatchers()
    },
    async mounted() {
        await this.loadInitializeData()
        this.localModal = !!localStorage.getItem('successModal')
        await this.getMerchantSetup()
        await this.getProvinces()
        await this.getBranchList()
        await this.getFulfillment()
        await this.getFulfillmentFees()

    },
    watch: {
        merchantStatus: {
            handler(newMerchantStatus) {
                const isStep1Completed = newMerchantStatus.progress[0].step_status === "Completed";
                const isStep2Completed = newMerchantStatus.progress[1].step_status === "Completed";
                const storeLocationCompleted = newMerchantStatus.progress[0].substeps[0].status === "Completed"
                this.collapseDropdown = isStep1Completed

                if (isStep1Completed && isStep2Completed) {
                    this.$store.commit('onboarding/setModuleAvailableModal', true)
                    this.$store.commit('onboarding/setNavigationBar', true);
                    this.$store.commit('onboarding/setOnboardingModuleCompleted', true)

                }
                if (storeLocationCompleted) {
                    this.$store.commit('onboarding/setNavigationBarStore', true)
                }
            },
            deep: true,
        },
    },
    filters: {
        removeYet(value) {
            if (!value) return "";
            return value.replace(" yet", "");
        },
    },

}



</script>
<style lang="scss" scoped>
.link-loading {
    color: black;
    font-size: 20px;
    font-style: normal;
    line-height: normal;
    text-decoration-line: none;
    margin-left: 5px;
    animation: loadingAnimation 1s infinite;
    animation-delay: 0.2;

}

.loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    border: solid black 1px;
    width: 200px;

}

.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #333;
    /* Adjust color as needed */
    margin: 0 4px;
    /* Adjust spacing between dots */
    animation: loadingAnimation 1s infinite;
}

.dot1 {
    animation-delay: 0s;
}

.dot2 {
    animation-delay: 0.2s;
}

.dot3 {
    animation-delay: 0.4s;
}

@keyframes loadingAnimation {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-10px);
    }

    /* Adjust animation distance */
}

.disable-btn {
    cursor: not-allowed;
}

.title-container {
    margin-right: 10px;
}

.logistic-error {
    margin-top: 0px;
    margin-bottom: 20px;
    border-radius: 4px;
    border: 1px solid #FFA39E;
    background: #FFF1F0;
    display: flex;
    align-items: center;
    height: 48px;
    justify-content: space-between;
    padding: 0px 13px;

    .error-title {
        display: flex;
    }

    p {
        color: #2F3941;
        font-size: 14px;
        font-style: normal;
        line-height: 22px;
        margin: 0px;
        margin-left: 11px;
    }
}

.collapse-icon {
    @media(max-width: 706px) {
        display: none;
    }
}

.collapse-footer {
    line-height: 1.5;
}

.fulfillment-form-error {
    border: solid 2px red;

    .error-message {

        color: red;
    }
}

.spinner {
    animation: rotate 1.5s linear infinite;
    /* Add rotation animation */
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
        /* Start rotation from 0 degrees */
    }

    to {
        transform: rotate(360deg);
        /* Rotate 360 degrees (full circle) */
    }
}

a {
    &:hover {
        text-decoration: none;
    }
}

.onboarding-toast {
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 132px;
    padding: 0px 10px;
    height: 29px;
    position: fixed;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 38px;
    border: 1px solid #E9EBED;
    background: #FFF;
    box-shadow: 0px 2px 5px -3px rgba(67, 67, 67, 0.25);
    z-index: 99999;

    .success {
        color: #139961;
        font-size: 13px;
        font-style: normal;
        line-height: normal;
    }

    .error {
        color: red;
        font-size: 13px;
        font-style: normal;
        line-height: normal;
    }
}

.footer-link {
    margin-top: 20px;
    color: #2F3941;
    font-size: 14px;
    font-style: normal;
    line-height: 22px;
    margin-left: 20px;
    margin-bottom: 20px;


    .store-link {
        color: #157EE7;
        font-size: 14px;
        font-style: normal;
        line-height: normal;
        text-decoration-line: underline;
        cursor: pointer;
    }
}

.collapse-content {
    .store-link {
        color: #157EE7;
        font-size: 14px;
        font-style: normal;
        line-height: normal;
        text-decoration-line: underline;
        cursor: pointer;
    }
}

.custom-input-group {
    border-radius: 8px;
    /* Adjust the value as needed */
}

.fees-border-error {
    border: solid 2px red;
}

.custom-input-group .custom-prepend {
    border-radius: 8px 0 0 10px;
    padding: 0.375rem 0.75rem;
    background-color: #f0f0f0;
    display: flex;
    align-items: center;
    color: #959BAB;
    font-size: 14px;
    font-style: normal;
    line-height: normal;
}

.set-fees {
    border-radius: 8px;
}

.payout-details {
    margin-bottom: 20px;

    a {
        text-decoration: none;
        color: black;

        &:hover {
            color: black;
        }
    }
}

.fees-details {
    margin-top: 20px;
}

.fulfillment-link {
    margin-left: 5px;
}

.required-sign {
  color: #2A2A2A;
    font-size: 13px;
    font-style: normal;
    line-height: normal;
    margin-top: 15px;
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 8px;
    display: flex;
    justify-items: center;

    span {
        margin-top: 3px;
        margin-left: 5px;
    }

}

.fees-content {
    width: 443px;


    @media(max-width: 1068px) {
        width: 400px;
    }

    @media(max-width: 562px) {
        width: 250px;
    }

    div {
        margin-bottom: 5px
    }

}

.container-onboard {
    display: flex;
    padding: 50px 50px 0px 50px;
    width: 100%;
    height: 95vh;
    overflow-y: hidden;
    background-color: #f9f9f9;

    @media (max-width: 991px) {
        flex-direction: column-reverse;
    }

    @media(max-width: 500px) {
        padding: 10px;
    }


    .location-form {
        width: 80%;
        margin-bottom: 20px;
    }

    .collapse-content {
        width: 100%;
        padding: 20px;
        font-size: 14px;
        line-height: normal;

        .substeps-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            border: none;
            padding: 14px 16px;
            border-radius: 8px;
            background: #53B6B2;
            margin-top: 15px;
            width: 78px;

            .plus-icon {
                width: 24px;
                height: 24px;
                margin-right: 10px;

                img {
                    width: 20px;
                    height: 20px;
                    margin-bottom: 4px;
                }
            }

            .arrow-icon {
                transform: rotate(90deg);
                width: 24px;
                height: 24px;
                margin-left: 10px;

                img {
                    height: 20px;
                    width: 20px;
                }
            }

        }

        .docu {
            width: fit-content;
        }

        .fulfillment-item {
            display: flex;
            justify-items: center;
            align-items: center;
            justify-content: space-between;
            width: 40%;

            @media(max-width: 1246px) {
                width: 60%;
            }

            @media(max-width: 991px) {
                width: 40%;
            }

            @media(max-width: 800px) {
                width: 60%;
            }

            @media(max-width: 424px) {
                width: 80%;
            }

            div {
                margin-bottom: 15px;
            }

        }
    }

    .onboard-header-section {
        margin-bottom: 60px;

    }

    .flex-item:first-child {
        flex: 1;
        overflow-y: scroll;
        padding-left: 24px;
        scrollbar-width: none;

        @media(max-width: 706px) {
            padding-left: 0px;
        }

    }

    .sub-title {
        margin-top: 12px;
        font-size: 12px;
    }

    .flex-item {
        font-style: normal;
        line-height: normal;

        .subtitle-onboard {
            color: #68737d;
            font-size: 14px;
            margin-bottom: 0px;
        }

        .title-onboard {
            font-size: 18px;
            margin-bottom: 10px;
        }

        .step-wrapper {
            border-left: #d8dcde solid 1px;
            padding: 40px;
            padding-top: 0;
            padding-bottom: 50px;

            @media(max-width: 706px) {
                border: none;
                padding: 0px;
            }



            .step {
                margin-top: -30px;
                margin-left: -65px;
                display: flex;
                justify-content: space-between;


                @media(max-width: 706px) {
                    margin-top: 0px;
                    margin-left: 0px;
                }

                .inner-step {
                    display: flex;
                    align-content: center;

                    .shop-icon {
                        margin-right: 25px;

                        @media(max-width: 706px) {
                            display: none;
                        }
                    }

                    .shop-instruction {
                        margin-top: 15px;

                        .shop-configure {
                            color: #2f3941;
                            font-size: 18px;

                            .option {
                                color: #87929D;
                                text-align: center;
                                font-size: 14px;
                                font-style: normal;
                                line-height: normal;
                            }
                        }

                        .shop-configure-sub {
                            color: #68737d;
                            font-size: 14px;
                            line-height: 1.5;
                        }
                    }
                }

                .start-indicator {
                    margin-top: 10px;
                    padding: 5px 7px;
                    border-radius: 28px;
                    border: 1px solid #d8dcde;
                    background: #f3f3f3;
                    color: #87929d;
                    font-size: 13px;
                    line-height: normal;
                    min-width: 100px;
                    text-align: center;
                }

                .started {
                    color: #B58302;
                    border: 1px solid #ECD89F;
                    background: #FFF3D2;
                }

                .completed {
                    border: 1px solid #9CDAC0;
                    background: #E4F4ED;
                    color: #14784E;
                }
            }

            .item {
                margin-bottom: 15px;
            }
        }

        .no-border-left {
            border: none;
        }
    }

    .flex-item:last-child {
        flex: 0.3;
    }

    .steps {
        min-width: 100%;
        border: 1px solid red;
    }
}
</style>
